import * as React from 'react';

import { Badge, DataGridColumn, LocalizedDate } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { PaymentAccountColumnFilter } from '../filters/PaymentAccountColumnFilter';
import { PaymentDateRangeColumnFilter } from '../filters/PaymentDateRangeColumnFilter';
import { PaymentFinancialAccountColumnFilter } from '../filters/PaymentFinancialAccountColumnFilter';
import { PaymentMethodColumnFilter } from '../filters/PaymentMethodColumnFilter';
import { PaymentStatusColumnFilter } from '../filters/PaymentStatusColumnFilter';
import { PaymentTypeColumnFilter } from '../filters/PaymentTypeColumnFilter';
import { PaymentActionMenu } from '../PaymentActionMenu';
import { PaymentLinkedDocuments } from '../PaymentLinkedDocuments';
import { PaymentStatusBadge } from '../PaymentStatusBadge';

import { useTranslations } from '@/finance/i18n/useTranslations';
import {
  $columnDateRange,
  $columnSelectedAccountIds,
  $columnSelectedFinancialAccountIds,
  $columnSelectedMethods,
  $columnSelectedStatuses,
  $columnSelectedTypes,
} from '~/modules/finance/_state/financePayments.state';
import { PaymentRecord } from '~/modules/finance/types';
import { getLocale } from '~/shared/utils/locale';

type Args = {
  onApplyAdvance?: (payment: PaymentRecord) => void;
  onDoubleClick?: (record: PaymentRecord) => void;
};

export const usePaymentsListColumns = ({
  onApplyAdvance,
  onDoubleClick,
}: Args) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const columnSelectedStatuses = useAtomValue($columnSelectedStatuses);
  const columnSelectedTypes = useAtomValue($columnSelectedTypes);
  const columnSelectedMethods = useAtomValue($columnSelectedMethods);
  const columnSelectedAccountIds = useAtomValue($columnSelectedAccountIds);
  const columnSelectedFinancialAccountIds = useAtomValue(
    $columnSelectedFinancialAccountIds,
  );
  const columnDateRange = useAtomValue($columnDateRange);

  const columns: DataGridColumn<PaymentRecord>[] = React.useMemo(
    () => [
      {
        key: 'transactionDate',
        header: t.columns.date,
        sortable: true,
        cell: ({ row: record }) => (
          <span>
            <LocalizedDate date={record.transactionDate} dateStyle="short" />
          </span>
        ),
        columnFilter: ({ close }) => (
          <PaymentDateRangeColumnFilter onClose={close} />
        ),
        isFilterActive: !!columnDateRange,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'paymentType',
        header: t.columns.type,
        cell: ({ row: record }) => (
          <Badge
            color={record.paymentType.includes('Advance') ? 'teal' : 'neutral'}
          >
            {t.enums.paymentType[record.paymentType] ?? record.paymentType}
          </Badge>
        ),
        columnFilter: ({ close }) => (
          <PaymentTypeColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedTypes.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'accountName',
        header: t.columns.account,
        resizable: true,
        grow: 1,
        cell: ({ row: record }) => record.accountName ?? '-',
        columnFilter: ({ close }) => (
          <PaymentAccountColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedAccountIds.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'paymentMethod',
        header: t.columns.method,
        cell: ({ row: record }) => (
          <span>
            {t.payments.list.methodFilter.options[record.paymentMethod] ??
              record.paymentMethod}
          </span>
        ),
        columnFilter: ({ close }) => (
          <PaymentMethodColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedMethods.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'financialAccount',
        header: t.payments.list.columns.financialAccount,
        width: 180,
        cell: ({ row: record }) => (
          <span className="truncate" title={record.financialAccount?.name}>
            {record.financialAccount?.name ?? '-'}
          </span>
        ),
        columnFilter: ({ close }) => (
          <PaymentFinancialAccountColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedFinancialAccountIds.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'status',
        header: t.columns.status,
        sortable: true,
        cell: ({ row: record }) => (
          <PaymentStatusBadge status={record.status} />
        ),
        columnFilter: ({ close }) => (
          <PaymentStatusColumnFilter onClose={close} />
        ),
        isFilterActive: columnSelectedStatuses.size > 0,
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'amount',
        header: t.columns.amount,
        sortable: true,
        cell: ({ row: record }) => (
          <span
            className={
              record.amount.amount < 0 ? 'text-failure/60' : 'text-success/60'
            }
          >
            {new Intl.NumberFormat(getLocale(), {
              style: 'currency',
              currency: record.amount.currencyCode,
            }).format(record.amount.amount)}
          </span>
        ),
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'unappliedAmount',
        header: t.columns.unapplied,
        cell: ({ row: record }) => {
          if (record.unappliedAmount.amount === 0) {
            return <span className="text-neutral/60">-</span>;
          }
          return (
            <span className="text-warning">
              {new Intl.NumberFormat(getLocale(), {
                style: 'currency',
                currency: record.unappliedAmount.currencyCode,
              }).format(Math.abs(record.unappliedAmount.amount))}
            </span>
          );
        },
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'linkedDocuments',
        header: t.payments.list.columns.linkedDocuments,
        width: 200,
        cell: ({ row: record }) => (
          <PaymentLinkedDocuments
            linkedBills={record.linkedBills}
            linkedInvoices={record.linkedInvoices}
          />
        ),
        onDoubleClick: ({ row }) => onDoubleClick?.(row),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row: record }) => (
          <PaymentActionMenu
            payment={record}
            onApplyAdvance={onApplyAdvance}
            onViewDetails={(payment) => onDoubleClick?.(payment)}
          />
        ),
      },
    ],
    [
      onApplyAdvance,
      onDoubleClick,
      t,
      columnSelectedStatuses,
      columnSelectedTypes,
      columnSelectedMethods,
      columnSelectedAccountIds,
      columnSelectedFinancialAccountIds,
      columnDateRange,
    ],
  );

  return { columns };
};
